import React, { useState, useEffect } from 'react';
import { FormikProvider, FieldArray } from 'formik';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Divider,
  Button,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import OrderFileMultiFileForm from './OrderFileMultiFileForm';

import 'dayjs/locale/ko'; 
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'; 
import DateAdapter from '@mui/lab/AdapterDateFns';
import * as dayjs from 'dayjs'


/**
 * 달력에서 받은 값을 정리한다.
 *
 * 날짜를 지우면 null 이 온다. 그대로 dayjs(null) 을 만들면 Invalid Date 가 되고,
 * 검증은 "must be a `date` type" 이라는 영문 메시지로 막힌다. 그 메시지는 화면에
 * 뜨지도 않아서, 사용자에게는 저장 버튼이 죽은 것처럼 보였다. 비었으면 null 로 둔다.
 */
const toDay = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d : null;
};

const OrderBookForm = ({ type, orderInfo, chance, formik }) => {
  const [siDt, setSiDt] = useState(null);
  const [expDay, setExpDay] = useState(null);
  const [mtnFreeStrtDt, setMtnFreeStrtDt] = useState(null);
  const [mtnFreeEndDt, setMtnFreeEndDt] = useState(null);

  const [licStrtDt, setLicStrtDt] = useState(null);
  const [licEndDt, setLicEndDt] = useState(null);

  const [dlvryDuedate, setDlvryDuedate] = useState(null);
  
  // const [giPrePct, setGiPrePct] = useState(null);
  // const [giGovPct, setGiGovPct] = useState(null);
  // const [giMtnPct, setGiMtnPct] = useState(null);

  
  // giPrePct: values.giPrePct, //보증보험(선급금 비율율)
  // giGovPct: values.giGovPct, //보증보험(계약이행 비율)
  // giMtnPct: values.giMtnPct, //보증보험(하자유지보수 비율)


  useEffect(() => {

    if (orderInfo) {
      setSiDt(orderInfo.siDt);
      setExpDay(orderInfo.expDay);
      
      setMtnFreeStrtDt(orderInfo.mtnFreeStrtDt);
      setMtnFreeEndDt(orderInfo.mtnFreeEndDt);

      if( orderInfo.dlvryDuedate )setDlvryDuedate(orderInfo.dlvryDuedate);

      if( orderInfo.licStrtDt ) setLicStrtDt(orderInfo.licStrtDt);
      if( orderInfo.licEndDt ) setLicEndDt(orderInfo.licEndDt);

      // 화면 달력은 위 로컬 값으로 그려지는데 formik 값은 비어 있었다. 그래서 날짜가 보이는데도
      // "납품 예정일을 입력하세요"로 저장이 막히거나(2026-10-14 현장), 저장 시 빈 값으로 덮였다.
      // formik 이 비어 있을 때만 화면에 보이는 값을 넣어 둘을 맞춘다(사용자가 바꾼 값은 건드리지 않음).
      ['dlvryDuedate', 'licStrtDt', 'licEndDt'].forEach((name) => {
        if (orderInfo[name] && !formik.values[name]) {
          formik.setFieldValue(name, orderInfo[name], false);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderInfo]);

  const onChangeDate = (newValue, name) => {
    formik.setFieldValue(name, newValue);
  };

  return (
    <>
      <Box sx={{ mt: 1, px: 2 }}>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}
        >
          <Grid item xs={1}>
            <Typography variant="subtitle2" component="div">
              프로젝트 번호
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" component="div">
              {orderInfo?.prjNo}
            </Typography>
          </Grid>
          <Grid item xs={1}>
            <Typography variant="subtitle2" component="div">
              사업명
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" component="div">
              {type === 'add' ? orderInfo?.cstNm : orderInfo?.prjNm}
            </Typography>
          </Grid>
          <Grid item xs={1}>
            <Typography variant="subtitle2" component="div">
              매출처
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body2" component="div">
              {orderInfo?.partnerNm}
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Box>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
        >
          <Grid item xs={3}>
            <LocalizationProvider  dateAdapter={AdapterDayjs} adapterLocale="ko">
              <DatePicker
                label="계약 시작일"
                format="YYYY-MM-DD"
                mask={'____-__-__'}
                id="expDay"
                name="expDay"
                value={toDay(expDay)}
                onChange={(newValue) => {
                  setExpDay(toDay(newValue));
                  onChangeDate(toDay(newValue), 'expDay');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={
                      formik.touched.expDay && Boolean(formik.errors.expDay)
                    }
                    helperText={formik.touched.expDay && formik.errors.expDay}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
              <DatePicker
                label="계약 완료일"
                format="YYYY-MM-DD"
                mask={'____-__-__'}
                id="siDt"
                name="siDt"
                value={toDay(siDt)}
                onChange={(newValue) => {
                  setSiDt(toDay(newValue));
                  onChangeDate(toDay(newValue), 'siDt');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={formik.touched.siDt && Boolean(formik.errors.siDt)}
                    helperText={formik.touched.siDt && formik.errors.siDt}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          {chance?.prjTyp === 'N' && (
            <>
              <Grid item xs={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko"
                >
                  <DatePicker
                    label="무상유지보수 시작일"
                    format="YYYY-MM-DD"
                    mask={'____-__-__'}
                    id="mtnFreeStrtDt"
                    name="mtnFreeStrtDt"
                    value={toDay(mtnFreeStrtDt)}
                    onChange={(newValue) => {
                      setMtnFreeStrtDt(toDay(newValue));
                      onChangeDate(toDay(newValue), 'mtnFreeStrtDt');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        error={
                          formik.touched.mtnFreeStrtDt &&
                          Boolean(formik.errors.mtnFreeStrtDt)
                        }
                        helperText={
                          formik.touched.mtnFreeStrtDt &&
                          formik.errors.mtnFreeStrtDt
                        }
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko"
                >
                  <DatePicker
                    label="무상유지보수 종료일"
                    format="YYYY-MM-DD"
                    mask={'____-__-__'}
                    id="mtnFreeEndDt"
                    name="mtnFreeEndDt"
                    value={toDay(mtnFreeEndDt)}
                    onChange={(newValue) => {
                      setMtnFreeEndDt(toDay(newValue));
                      onChangeDate(toDay(newValue), 'mtnFreeEndDt');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        fullWidth
                        margin="normal"
                        variant="outlined"
                        error={
                          formik.touched.mtnFreeEndDt &&
                          Boolean(formik.errors.mtnFreeEndDt)
                        }
                        helperText={
                          formik.touched.mtnFreeEndDt &&
                          formik.errors.mtnFreeEndDt
                        }
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </>
          )}

          <Grid item xs={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
              <DatePicker
                label="라이센스 시작일"
                format="YYYY-MM-DD"
                mask={'____-__-__'}
                id="licStrtDt"
                name="licStrtDt"
                value={toDay(licStrtDt)}
                onChange={(newValue) => {
                  setLicStrtDt(toDay(newValue));
                  onChangeDate(toDay(newValue), 'licStrtDt');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={
                      formik.touched.licStrtDt && Boolean(formik.errors.licStrtDt)
                    }
                    helperText={formik.touched.licStrtDt && formik.errors.licStrtDt}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
              <DatePicker
                label="라이센스 만료일"
                format="YYYY-MM-DD"
                mask={'____-__-__'}
                id="licEndDt"
                name="licEndDt"
                value={toDay(licEndDt)}
                onChange={(newValue) => {
                  setLicEndDt(toDay(newValue));
                  onChangeDate(toDay(newValue), 'licEndDt');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={formik.touched.licEndDt && Boolean(formik.errors.licEndDt)}
                    helperText={formik.touched.licEndDt && formik.errors.licEndDt}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
              <DatePicker
                label="납품 예정일"
                format="YYYY-MM-DD"
                mask={'____-__-__'}
                id="dlvryDuedate"
                name="dlvryDuedate"
                value={toDay(dlvryDuedate)}
                onChange={(newValue) => {
                  // 라이센스 시작일 setter 를 부르고 있었다(복붙). 입력한 날짜가 라이센스
                  // 시작일 칸으로 들어가고 납품 예정일은 끝까지 비어서 DLVRY_DUE_DT 가 NULL 로
                  // 저장됐다. 그러면 납기 OTD 가 수주 납기 대신 생산계획 완료예정일을 본다.
                  setDlvryDuedate(toDay(newValue));
                  onChangeDate(toDay(newValue), 'dlvryDuedate');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    error={
                      formik.touched.dlvryDuedate && Boolean(formik.errors.dlvryDuedate)
                    }
                    helperText={formik.touched.dlvryDuedate && formik.errors.dlvryDuedate}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={3}>
          <TextField
              margin="normal"
              variant="outlined"
              required={false}
              fullWidth
              id="inspectCond"
              name="inspectCond"
              label="검수 조건"
              value={formik.values.inspectCond}
              onChange={formik.handleChange}
              error={
                formik.touched.inspectCond && Boolean(formik.errors.inspectCond)
              }
              helperText={formik.touched.inspectCond && formik.errors.inspectCond}
            />
          </Grid>


          <Grid item xs={2}>
            <TextField
              margin="normal"
              variant="outlined"
              required={false}
              fullWidth
              id="giPrePct"
              name="giPrePct"
              label="보증보험(선급금 비율)"
              value={formik.values.giPrePct}
              onChange={formik.handleChange}
              error={
                formik.touched.giPrePct && Boolean(formik.errors.giPrePct)
              }
              helperText={formik.touched.giPrePct && formik.errors.giPrePct}
            />
        </Grid>

        <Grid item xs={2}>
            <TextField
              margin="normal"
              variant="outlined"
              required={false}
              fullWidth
              id="giCovPct"
              name="giCovPct"
              label="보증보험(계약이행 비율)"
              value={formik.values.giCovPct}
              onChange={formik.handleChange}
              error={
                formik.touched.giCovPct && Boolean(formik.errors.giCovPct)
              }
              helperText={formik.touched.giCovPct && formik.errors.giCovPct}
            />
        </Grid>

        <Grid item xs={2}>
            <TextField
              margin="normal"
              variant="outlined"
              required={false}
              fullWidth
              id="giMtnPct"
              name="giMtnPct"
              label="보증보험(하자유지보수 비율)"
              value={formik.values.giMtnPct}
              onChange={formik.handleChange}
              error={
                formik.touched.giMtnPct && Boolean(formik.errors.giMtnPct)
              }
              helperText={formik.touched.giMtnPct && formik.errors.giMtnPct}
            />
        </Grid>

        </Grid>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
        >
          <Grid item xs={6}>
            <TextField
              margin="normal"
              variant="outlined"
              required={false}
              fullWidth
              id="collCondt"
              name="collCondt"
              label="수금조건"
              value={formik.values.collCondt}
              onChange={formik.handleChange}
              error={
                formik.touched.collCondt && Boolean(formik.errors.collCondt)
              }
              helperText={formik.touched.collCondt && formik.errors.collCondt}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              margin="normal"
              variant="outlined"
              required={false}
              fullWidth
              id="payCondt"
              name="payCondt"
              label="지급조건"
              value={formik.values.payCondt}
              onChange={formik.handleChange}
              error={formik.touched.payCondt && Boolean(formik.errors.payCondt)}
              helperText={formik.touched.payCondt && formik.errors.payCondt}
            />
          </Grid>
        </Grid>
        <Grid
          container
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
          sx={{ py: 1 }}
        >
          {/* <Grid item xs={2}>
            <Typography variant="subtitle2" component="div" textAlign="center">
              참고사항
            </Typography>
          </Grid> */}
          <Grid item xs={12}>
            <TextField
              margin="normal"
              variant="outlined"
              fullWidth
              id="comment"
              name="comment"
              label="참고사항"
              multiline
              rows={3}
              value={formik.values.comment}
              onChange={formik.handleChange}
              error={formik.touched.comment && Boolean(formik.errors.comment)}
              helperText={formik.touched.comment && formik.errors.comment}
            />
          </Grid>
        </Grid>
      </Box>
      {/* <Divider sx={{ my: 1 }} />
      <Box
        sx={{
          px: 1,
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
        }}
      >
        <Box sx={{ mr: 1 }}>
          <FileUploadIcon />
        </Box>
        <Typography variant="subtitle2">파일 업로드</Typography>
      </Box>
      <FormikProvider value={formik}>
        <FieldArray
          name="files"
          render={(arrayHelpers) => (
            <Box>
              <Box sx={{ my: 1 }}>
                {formik.values?.files.map((f, index) => {
                  return (
                    <OrderFileMultiFileForm
                      key={index}
                      file={f}
                      index={index}
                      touched={formik.touched}
                      errors={formik.errors}
                      handleChange={formik.handleChange}
                      handleBlur={formik.handleBlur}
                      handleRemove={() => arrayHelpers.remove(index)}
                    />
                  );
                })}
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 2,
                  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                }}
              >
                <Button
                  sx={{ mx: 2 }}
                  color="secondary"
                  variant="outlined"
                  onClick={() =>
                    arrayHelpers.push({ fileDesc: '', file: null })
                  }
                >
                  파일 추가
                </Button>
              </Box>
            </Box>
          )}
        ></FieldArray>
      </FormikProvider> */}
    </>
  );
};

export default OrderBookForm;
